import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./ApprovedPrView.css";
import { useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { IoMdArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import "react-datepicker/dist/react-datepicker.css";
import API_URL from "../../src/Config";
import "./voyageView.css";
import { FiEye, FiDownload } from "react-icons/fi";

function MonthlyInventoryView({ isSidebarExpanded }) {
  const [monthlyInventoryData, setmonthlyInventoryData] = useState([]);
  const { id } = useParams();

  // Snackbar states
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [groupedAttachments, setGroupedAttachments] = useState({});
  const navigate = useNavigate();

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleBackVoyageList = () => {
    navigate("/monthly-inventory-report-list");
  };

  const fetchData = async () => {
    try {
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/inventory/monthly_report/${id}`,
        {
          headers: {
            Authorization: authKey,
          },
        }
      );

      const data = response?.data;
      setmonthlyInventoryData(data);

      setGroupedAttachments(data?.grouped_attachments);

      const attachments = data?.attachments || [];
    } catch (error) {
      console.error("❌ Error fetching voyage data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Download attachment function
  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/atf/download_monthly_report_attachment/${fileId}`,
        {
          headers: {
            Authorization: authKey,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar("File downloaded successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      setSeverity("error");
      showSnackbar("Failed to download file");
    }
  };

  // View attachment function
  const handleViewAttachment = async (fileId) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const viewUrl = `${API_URL}/api/atf/view_monthly_report_attachment/${fileId}`;

      const response = await axios.get(viewUrl, {
        headers: {
          Authorization: authKey,
        },
        responseType: "blob",
      });

      const contentType =
        response.headers["content-type"] || "application/octet-stream";

      const blob = new Blob([response.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);

      const newWindow = window.open("", "_blank");

      if (contentType.startsWith("image/")) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Image Preview</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                  background-color: #f0f0f0;
                }
                img { 
                  max-width: 100%; 
                  max-height: 100vh; 
                  object-fit: contain;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
              </style>
            </head>
            <body>
              <img src="${fileURL}" alt="File Preview" />
            </body>
          </html>
        `);
      } else if (contentType === "application/pdf") {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>PDF Preview</title>
              <style>
                body { margin: 0; padding: 0; }
                iframe { width: 100%; height: 100vh; border: none; }
              </style>
            </head>
            <body>
              <iframe src="${fileURL}" type="application/pdf"></iframe>
            </body>
          </html>
        `);
      } else if (contentType.startsWith("text/")) {
        const text = await response.data.text();
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Text Preview</title>
              <style>
                body { 
                  font-family: monospace; 
                  padding: 20px; 
                  background-color: #f8f8f8;
                  line-height: 1.4;
                }
                pre { 
                  white-space: pre-wrap; 
                  word-wrap: break-word; 
                }
              </style>
            </head>
            <body>
              <pre>${text}</pre>
            </body>
          </html>
        `);
      } else {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>File Preview</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  padding: 20px; 
                  text-align: center;
                  background-color: #f0f0f0;
                }
                .container {
                  max-width: 400px;
                  margin: 50px auto;
                  padding: 30px;
                  background: white;
                  border-radius: 8px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }
                .download-btn {
                  background-color: #007bff;
                  color: white;
                  padding: 10px 20px;
                  border: none;
                  border-radius: 4px;
                  cursor: pointer;
                  text-decoration: none;
                  display: inline-block;
                  margin-top: 20px;
                }
                .download-btn:hover {
                  background-color: #0056b3;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <h3>File Preview</h3>
                <p>This file type cannot be previewed directly.</p>
                <p>Content Type: ${contentType}</p>
                <a href="${fileURL}" download class="download-btn">Download File</a>
              </div>
            </body>
          </html>
        `);
      }

      newWindow.document.close();

      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 10000);
    } catch (error) {
      console.error("Error viewing file:", error);
      setSeverity("error");
      showSnackbar("Failed to view file");
    }
  };

  const docTypeLabels = {
    ac2a_doc: "AC2A Document",
    bunkering_summary_doc: "Bunkering Summary Document",
    physical_inventory_certificate: "Physical Inventory Certificate",
    supplies_to_coast_guards_doc: "Supplies to Coast Guards Document",
    supplies_to_navy_doc: "Supplies to Navy Document",
    supply_to_ro_doc: "Supply to RO Document",
    supportive_bunkering_summary: "Supportive Bunkering Summary",
    supportive_supplies_to_coast_guards: "Supportive Supplies to Coast Guards",
    supportive_supplies_to_navy: "Supportive Supplies to Navy",
    supportive_supply_to_ro: "Supportive Supply to RO",
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <>
      <div
        className={`invoice-request-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="invoice-request-header-content">
          <div className="invoice-request-heading-content-left-side">
            <Tooltip title="Back" arrow>
              <IoMdArrowBack
                className="back-company"
                onClick={handleBackVoyageList}
              />
            </Tooltip>
            Monthly Inventory Report Details
          </div>
        </div>

        {/* <div className=""> */}
        <div className="data-form-body-voyage">
          <div className="voyage-header-content">
            {monthlyInventoryData ? (
              <div className="project-detailss">
                <div className="project-detail-arround">
                  <div className="project-details-left-side">
                    <div className="project-detail">
                      <div className="detail-header">Month:</div>
                      <div className="detail-part-voyage">
                        {monthlyInventoryData?.month
                          ? monthNames[monthlyInventoryData?.month - 1]
                          : "N/A"}
                      </div>
                    </div>

                    <div className="project-detail">
                      <div className="detail-header">Year:</div>
                      <div className="detail-part-voyage">
                        {monthlyInventoryData.year || "N/A"}
                      </div>
                    </div>

                    <div className="project-detail">
                      <div className="detail-header">Status:</div>
                      <div className="detail-part-voyage">
                        {monthlyInventoryData.status || "N/A"}
                      </div>
                    </div>

                    <div className="project-detail">
                      <div className="detail-header">Description:</div>
                      <div className="detail-part-voyage">
                        {monthlyInventoryData.description || "N/A"}
                      </div>
                    </div>

                    <div className="project-detail">
                      <div className="detail-header">Created At:</div>
                      <div className="detail-part-voyage">
                        {monthlyInventoryData.created_at
                          ? new Date(monthlyInventoryData.created_at)
                              .toUTCString()
                              .split("GMT")[0]
                              .trim() + " IST"
                          : "NA"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p>Loading...</p>
            )}
          </div>
          <br />

          {Object.keys(groupedAttachments).length > 0 ? (
            Object.entries(groupedAttachments).map(([docType, files]) => (
              <div className="product_dflex" key={docType}>
                <div className="product_label">
                  <label>
                    {docTypeLabels[docType] || docType.replace(/_/g, " ")} :
                  </label>
                </div>
                <div style={{ width: "100%" }}>
                  {files.map((file, idx) => (
                    <div
                      className="uploaded-file-card"
                      key={`${file.file_id}-${idx}`}
                    >
                      <div className="file-info">
                        <div className="file-name">
                          File:
                          <span className="filename_span">
                            {file.filename || file.file_id}
                          </span>
                        </div>
                        <div className="file-meta">
                          {file.file_type?.toUpperCase()} | {file.file_size_kb}{" "}
                          KB
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          className="file-action-btn"
                          onClick={() => handleViewAttachment(file.file_id)}
                        >
                          <FiEye /> View
                        </button>
                        <button
                          className="file-action-btn"
                          onClick={() =>
                            handleDownloadAttachment(
                              file.file_id,
                              file.filename
                            )
                          }
                        >
                          <FiDownload /> Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-documents-note">
              <p>
                <strong>Note:</strong> No grouped attachments found yet.
              </p>
            </div>
          )}
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default MonthlyInventoryView;
